import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ModalAdminEditAnalysis from './ModalAdminEditAnalysis';
import { fetchApiRaw, GET_ANALYSES } from 'r/utils/fetchApiRaw';
import type { Analyse } from 'r/types';

export default function AdminHistoryEditRoute() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const cachedRaw = queryClient
    .getQueriesData<{ data: Analyse[] }>({ queryKey: ['admin-analyses'] })
    .flatMap(([, page]) => page?.data ?? [])
    .find((a) => a.id === analysisId);

  const { data: fetchedRaw } = useQuery({
    queryKey: ['admin-analysis', analysisId],
    queryFn: async () => {
      const res = await fetchApiRaw(GET_ANALYSES, {
        queryParams: {
          filter: encodeURIComponent(`"id":["${analysisId}"]`),
          take: '1',
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return (data[0] as Analyse) ?? null;
    },
    enabled: !cachedRaw && !!analysisId,
    staleTime: 5 * 60 * 1000,
  });

  const rawAnalysis = cachedRaw ?? fetchedRaw ?? null;

  const handleClose = () => {
    if (location.state?.background) {
      navigate(-1);
    } else {
      navigate('/?tab=history');
    }
  };

  const analysis =
    rawAnalysis ?
      {
        id: rawAnalysis.id,
        pair: { symbol: rawAnalysis.pair },
        isPredictionUp: rawAnalysis.isPredictionUp,
        tweetData: {
          name: rawAnalysis.authorName,
          id: rawAnalysis.authorUsername,
          text: rawAnalysis.tweetText,
        },
      }
    : null;

  return (
    <ModalAdminEditAnalysis
      isOpen={true}
      analysis={analysis}
      onClose={handleClose}
    />
  );
}
